/** Turns the raw `detail` JSON of an inbox item into readable Dutch text. Never shows raw JSON. */

export interface InboxText {
  /** Replaces the stored title when the detail allows a clearer one; null = keep the title. */
  headline: string | null;
  /** One explanatory sentence. */
  summary: string | null;
  lines: Array<{ label: string; value: string; mono?: boolean }>;
}

const EMPTY: InboxText = { headline: null, summary: null, lines: [] };

function parseObject(detail: string | null): Record<string, unknown> | { plain: string } | null {
  if (!detail || !detail.trim()) return null;
  try {
    const v: unknown = JSON.parse(detail);
    if (v && typeof v === "object" && !Array.isArray(v)) return v as Record<string, unknown>;
    if (typeof v === "string") return { plain: v };
    return null;
  } catch {
    // not JSON: only show it when it is short, human-looking text
    const t = detail.trim();
    return t.length <= 200 && !/^[[{]/.test(t) ? { plain: t } : null;
  }
}

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v.trim() : typeof v === "number" ? String(v) : null;
}

const SIDE_LABEL: Record<"production" | "sandbox", string> = { production: "Productie-URL", sandbox: "Sandbox-URL" };

export function inboxText(type: string, detail: string | null): InboxText {
  const o = parseObject(detail);
  if (!o) return EMPTY;
  if ("plain" in o && typeof o.plain === "string" && Object.keys(o).length === 1) return { ...EMPTY, summary: o.plain };
  const d = o as Record<string, unknown>;

  switch (type) {
    case "app_discovered": {
      const bundle = str(d.bundleId);
      const lines: InboxText["lines"] = [];
      const asc = str(d.ascAppId);
      const apple = str(d.appleAppId);
      if (asc) lines.push({ label: "App Store Connect-ID", value: asc, mono: true });
      if (apple) lines.push({ label: "Apple-app-ID", value: apple, mono: true });
      return {
        headline: bundle ? `Nieuwe app ontdekt: ${bundle}` : null,
        summary: "AppHQ heeft deze app automatisch gekoppeld; je hoeft niets in te stellen.",
        lines,
      };
    }
    case "url_conflict": {
      const lines: InboxText["lines"] = [];
      for (const side of ["production", "sandbox"] as const) {
        const url = str(d[side]);
        if (url) lines.push({ label: SIDE_LABEL[side], value: url, mono: true });
      }
      return {
        headline: null,
        summary: "Bij Apple staat al een notificatie-URL van een andere dienst. AppHQ overschrijft die niet zomaar. Kies \"Overnemen\" om AppHQ in te stellen, of \"Laten staan\" om de bestaande URL te houden.",
        lines,
      };
    }
    case "asc_conflict": {
      const reason = str(d.reason);
      return {
        headline: null,
        summary:
          reason === "conflict_409"
            ? "App Store Connect weigerde de wijziging omdat de app tegelijk is aangepast. De volgende sync probeert het opnieuw."
            : "App Store Connect gaf een conflict bij het instellen van de notificatie-URL. De volgende sync probeert het opnieuw.",
        lines: [],
      };
    }
    case "product_discovered": {
      const product = str(d.productId) ?? str(d.product_id);
      return { headline: null, summary: "Controleer of de gegevens van dit product kloppen.", lines: product ? [{ label: "Product-ID", value: product, mono: true }] : [] };
    }
    default:
      return EMPTY;
  }
}
